import { FC, useMemo, useState } from 'react';

import { EventFilter } from '@shared/types/event';
import dayjs from 'dayjs';
import { orderBy } from 'lodash';

import { useUnauthorizedEventsQuery, useUserEventsQuery } from '@queries/EventQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useEventMetaFilter } from '@hooks/EventHooks/useEventMetaFilter';
import { useAnalytics } from '@hooks/useAnalytics';

import { useNavigate } from '@router/index';

import Button from '@components/buttons/Button';
import { EventChip } from '@components/chips/EventChip';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import calendarIconUrl from '@screens/entertainer/EntertainerDetailsScreen/assets/calendar.svg';
import prideSvgUrl from '@screens/entertainer/EntertainerDetailsScreen/assets/pride.svg';
import { EventInvitePopup } from '@screens/event/EventRsvpScreen/EventInvitePopup';

import {
  DenverPride2026PrimaryCtaCard,
  PRIDE_WEEKEND_END_DATE,
  PRIDE_WEEKEND_START_DATE,
} from '../components/DenverPride2026PrimaryCtaCard';
import { EventRsvpCard } from '../components/EventRsvpCard';
import addSvgUrl from './image-gallery.svg';

const UserRsvpsCard: FC<{}> = ({}) => {
  const { currentUser } = useCurrentUser();
  const { eventsAreReady, events } = useUserEventsQuery();
  const { openAlertDialog } = useDialog();

  const userGoingRsvps = useMemo(() => {
    return events.going ?? [];
  }, [events]);

  const userConsideringRsvpsCount = useMemo(() => {
    return (events.maybe?.length ?? 0) + (events.interested?.length ?? 0);
  }, [events]);

  if (!currentUser) {
    return <></>;
    // return (
    //   <div>
    //     <PrimaryCtaCard
    //       cta={{
    //         img: calendarIconUrl,
    //         title: `Build your queer Denver calendar`,
    //         description: `Save events, see who's going, and never miss a night.`,
    //         url: '',
    //         onClick: () =>
    //           openAlertDialog({
    //             title: 'Denver Queer Calendar',
    //             text: `Use the "Attending" and "Interested" buttons to add upcoming events to your calendar.`,
    //           }),
    //         badgeCount: 1,
    //       }}
    //       track={{ event: 'Click User Rsvps Card' }}
    //     />
    //   </div>
    // );
  }

  if (!eventsAreReady) {
    return (
      <div>
        <Skeleton height={70} />
      </div>
    );
  }

  if (userGoingRsvps.length) {
    const description =
      userGoingRsvps
        .slice(0, 2)
        .map((event) => event.title)
        .join(', ') + (userGoingRsvps.length > 2 ? ` + ${userGoingRsvps.length - 2} more` : '');
    return (
      <div>
        <PrimaryCtaCard
          cta={{
            // img: currentUser?.avatar,
            img: calendarIconUrl,
            title: `You're going to ${userGoingRsvps.length} events`,
            description,
            url: `/noauth/ucc/${currentUser?._id}`,
          }}
          track={{ event: 'Click User Rsvps Card' }}
        />
      </div>
    );
  }

  if (userConsideringRsvpsCount) {
    return (
      <div>
        <PrimaryCtaCard
          cta={{
            // img: currentUser?.avatar,
            img: calendarIconUrl,
            title: `${userConsideringRsvpsCount} events you're considering`,
            description: `Tap to confirm or remove from your calendar.`,
            url: `/noauth/ucc/${currentUser?._id}`,
            badgeCount: userConsideringRsvpsCount,
          }}
          track={{ event: 'Click User Rsvps Card' }}
        />
      </div>
    );
  }

  return (
    <div>
      <PrimaryCtaCard
        cta={{
          // img: currentUser?.avatar,
          img: calendarIconUrl,
          title: `Nothing on your calendar yet`,
          description: `Tap the events below to add them to your weekend.`,
          url: '',
          onClick: () =>
            openAlertDialog({
              title: 'Denver Queer Calendar',
              text: `Use the "Attending" and "Interested" buttons to add upcoming events to your calendar.`,
            }),
          badgeCount: 1,
        }}
        track={{ event: 'Click User Rsvps Card' }}
      />
    </div>
  );
};

interface SeriesRsvpToEventsProps {
  cityId?: string;
  tagIds?: string[];
  tagSlug: string;
  back: string;
  initialFilterValue?: string;
  isSuperAdminMode?: boolean;
}

const FILTER_VALUES = ['isDanceParty', 'isSapphic', 'isDragShow', 'isPrideWeekend'];

export const SeriesRsvpToEvents: FC<SeriesRsvpToEventsProps> = ({
  cityId,
  tagIds,
  tagSlug,
  initialFilterValue,
  back,
  isSuperAdminMode = false,
}) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const [selectedInviteEventId, setSelectedInviteEventId] = useState<string>();
  const { createAuthGateHandler } = useAuthGate();
  const { currentUser } = useCurrentUser();
  const {
    eventsAreReady,
    events = [],
    eventsMeta,
  } = useUnauthorizedEventsQuery(
    {
      tagIds,
      startDate: dayjs().format('YYYY-MM-DD'),
      rsvpData: 'calculate',
    },
    Boolean(tagIds?.length),
    {
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
    },
  );

  const eventsWithPrideWeekendFilter = useMemo(() => {
    return events.map((event) => ({
      ...event,
      isPrideWeekend:
        dayjs(event.startDate).isSameOrAfter(PRIDE_WEEKEND_START_DATE, 'day') &&
        dayjs(event.startDate).isSameOrBefore(PRIDE_WEEKEND_END_DATE, 'day'),
    }));
  }, [events]);

  const filters = useMemo(() => {
    return [
      ...(eventsMeta?.filters ?? []),
      {
        value: 'isPrideWeekend',
        label: 'Pride Weekend',
      },
    ];
  }, [eventsMeta?.filters]);

  const { updateFilterValue, currentFilter, filteredEvents } = useEventMetaFilter(
    eventsWithPrideWeekendFilter,
    filters,
    initialFilterValue,
  );

  const trendingEvents = useMemo(
    () => orderBy(events, ['attending', 'interested'], ['desc', 'desc']).slice(0, 5),
    [events],
  );

  const isPrideWeekendOver = useMemo(() => {
    return dayjs().isAfter(PRIDE_WEEKEND_END_DATE, 'day');
  }, []);

  if (!eventsAreReady) {
    return <Skeleton height={300} />;
  }

  if (!events.length) {
    return <EmptyState title="No upcoming events" />;
  }

  const handleInviteClick = (eventId: string) => {
    track('Click Event Invite', { eventId });
    setSelectedInviteEventId(eventId);
  };

  return (
    <div>
      <div className="d-grid gap-2">
        <UserRsvpsCard />

        {tagSlug === 'denver-queer-calendar' && <DenverPride2026PrimaryCtaCard tagSlug={tagSlug} />}

        <PrimaryCtaCard
          cta={{
            img: addSvgUrl,
            title: 'Add Missing Event',
            description: `Don't see an event? Tap to add it to the calendar.`,
            url: `/noauth/series/${tagSlug}/add-rsvp-event`,
            onClick: () => {
              navigate(`/noauth/series/${tagSlug}/add-rsvp-event`, {
                state: {
                  back,
                },
              });
            },
          }}
          track={{ event: 'Click Add Event CTA' }}
        />
      </div>

      <TitleToolbar text="Trending Events" size="sm" />
      <HorizontalScroll
        items={trendingEvents.map((event) => (
          <EventRsvpCard
            key={event._id}
            event={event}
            variant="large"
            onInviteClick={() => handleInviteClick(event._id)}
            isSuperAdminMode={false}
          />
        ))}
      />
      <TitleToolbar
        text="Upcoming Events"
        size="sm"
        action={
          <Button
            className="py-1 px-3"
            size="sm"
            startIcon={<FontAwesomeIcon name="plus" />}
            onClick={() =>
              navigate(`/noauth/series/${tagSlug}/add-rsvp-event`, {
                state: {
                  back,
                },
              })
            }
          >
            Add Event
          </Button>
        }
      />
      <div className="mb-3">
        <HorizontalScroll
          items={[
            <div className="d-flex gap-1">
              <EventChip
                label="All"
                startIcon={<></>}
                variant={!currentFilter ? undefined : 'outlined'}
                onClick={() => updateFilterValue(undefined)}
                size="md"
              />
              {!isPrideWeekendOver && (
                <EventChip
                  label="Pride Weekend"
                  startIcon={
                    <img
                      src={prideSvgUrl}
                      style={{ height: 14, width: 14, objectFit: 'contain', display: 'block' }}
                    />
                  }
                  variant={currentFilter?.value === 'isPrideWeekend' ? undefined : 'outlined'}
                  onClick={() => updateFilterValue('isPrideWeekend')}
                  size="md"
                />
              )}
              {eventsMeta?.filters
                ?.filter((filter) => FILTER_VALUES.includes(filter.value))
                ?.map((filter) => (
                  <EventChip
                    key={filter.value}
                    label={filter.label}
                    startIcon={
                      <img
                        src={filter.cta?.suggestion?.iconUrl}
                        style={{ height: 14, width: 14, objectFit: 'contain', display: 'block' }}
                      />
                    }
                    variant={currentFilter?.value === filter.value ? undefined : 'outlined'}
                    size="md"
                    onClick={() => updateFilterValue(filter.value)}
                  />
                ))}
            </div>,
          ]}
        />
      </div>
      <div className="d-grid gap-3">
        {currentFilter && !filteredEvents.length ? (
          <EmptyState
            title="No matching events"
            text="No events match this filter"
            button={
              events.length
                ? { text: 'See all events', onClick: () => updateFilterValue(undefined) }
                : undefined
            }
          />
        ) : (
          filteredEvents.map((event) => (
            <EventRsvpCard
              key={event._id}
              event={event}
              variant="small"
              onInviteClick={() => handleInviteClick(event._id)}
              isSuperAdminMode={isSuperAdminMode}
              eventMetaFilters={eventsMeta?.filters}
            />
          ))
        )}
      </div>
      <EventInvitePopup
        isVisible={!!selectedInviteEventId}
        eventId={selectedInviteEventId}
        onClose={() => setSelectedInviteEventId(undefined)}
      />
    </div>
  );
};
